class TreatmentCountry
{
  // How long a locally-made choice outranks the account metafield, giving our
  // own (asynchronous) metafield write time to land and reach Liquid renders.
  // After this window the metafield is authoritative again, so a support-side
  // metafield fix is guaranteed to take effect on the customer's next visit.
  // (Getter, not a static class field — field syntax is a parse error on
  // Safari < 14.1 and would take the whole core.app bundle down with it.)
  static get PENDING_SYNC_SECONDS ()
  {
    return 600;
  }

  // THE single place that answers "which treatment country/state applies".
  // Works before init() has run (sections init from setTimeout(1) and can beat
  // app.init() on DOMContentLoaded), so section code must use this instead of
  // reading currentCountry/currentState or the cache directly.
  getSelection ()
  {
    // In-memory values reflect init()'s resolution plus any set()/setState()
    // made during this page load — always freshest once populated.
    if (this.currentCountry) {
      return {
        country: this.currentCountry,
        state: this.currentState || null
      };
    }

    return this.resolveFromSources();
  }

  // Resolve from the persisted sources: localStorage cache + the customer
  // metafields (Liquid render snapshot). The metafield is authoritative — a
  // support-side or other-device update must win on the next page load —
  // except while our own write is still propagating (see mergeWithMetafield).
  // Values no longer offered by the theme are dropped so they can't reach the
  // clinics/quota endpoints. No side effects; init() persists the outcome.
  resolveFromSources ()
  {
    const cache = window.app.cache;
    const customer = window.theme && window.theme.customer;

    const metafieldCountry = customer && customer.treatmentCountry
      ? ('' + customer.treatmentCountry).toUpperCase()
      : null;
    const metafieldState = customer && customer.treatmentState
      ? ('' + customer.treatmentState).toUpperCase()
      : null;

    let country = this.mergeWithMetafield(
      cache.get('treatment_country'),
      metafieldCountry,
      'treatment_country_pending_sync'
    );
    let state = this.mergeWithMetafield(
      cache.get('treatment_au_state'),
      metafieldState,
      'treatment_au_state_pending_sync'
    );

    // Drop values that are no longer offered (e.g. a delisted market, or an
    // AU state removed until we have clinics there). Fail open when the theme
    // lists are absent on this layout — a missing list must not clear a
    // legitimate selection.
    if (country && Array.isArray(window.theme.treatmentCountries) && window.theme.treatmentCountries.length
        && !window.theme.treatmentCountries.some(c => c.code === country)) {
      country = null;
    }

    if (state && Array.isArray(window.theme.auStates) && window.theme.auStates.length
        && !window.theme.auStates.some(s => s.code === state)) {
      state = null;
    }

    return { country: country, state: state };
  }

  // Merge one locally-cached value with its account-level metafield. The
  // metafield wins on disagreement UNLESS the pending-sync marker shows the
  // local value is one we pushed ourselves within the grace window — then the
  // Liquid snapshot is merely lagging behind us, not contradicting us.
  mergeWithMetafield (cachedValue, metafieldValue, pendingKey)
  {
    if (!metafieldValue) {
      return cachedValue;
    }

    if (!cachedValue || metafieldValue === cachedValue) {
      return metafieldValue;
    }

    return window.app.cache.get(pendingKey) === cachedValue
      ? cachedValue
      : metafieldValue;
  }

  init ()
  {
    this.popup = document.getElementById('popup-treatment');
    this.select = this.popup.querySelector('#popup-treatment-select');
    this.submit = this.popup.querySelector('.popup-treatment-footer-submit');
    this.stateContainer = this.popup.querySelector('.popup-treatment-state');
    this.stateSelect = this.popup.querySelector('#popup-treatment-state-select');

    const selection = this.resolveFromSources();
    this.currentCountry = selection.country;
    this.currentState = selection.state;

    // Persist the resolved values so the cache agrees with what we resolved
    // (heals localStorage after a metafield-side fix or a dropped value).
    if (this.currentCountry !== window.app.cache.get('treatment_country')) {
      if (this.currentCountry) {
        window.app.cache.forever('treatment_country', this.currentCountry);
      } else {
        window.app.cache.forget('treatment_country');
      }
    }

    if (this.currentState !== window.app.cache.get('treatment_au_state')) {
      if (this.currentState) {
        window.app.cache.forever('treatment_au_state', this.currentState);
      } else {
        window.app.cache.forget('treatment_au_state');
      }
    }

    this.currentCountryName = this.currentCountry
      ? this.getCountryName(this.currentCountry)
      : '';

    this.select.addEventListener('change', e => {
      this.update();
      this.syncPopupState();
    });

    this.submit.addEventListener('click', e => {
      e.preventDefault();

      if (this.submit.classList.contains('disabled')) {
        return;
      }

      this.set(this.select.value);

      if (this.currentCountry === 'AU' && this.stateSelect) {
        this.setState(this.stateSelect.value);
      }

      this.update();
      this.popup.close();
    });

    onBroadcast('treatment_country', data => {
      this.update();
    });

    this.syncPopupState();

    if (!this.currentCountry) {
      // Only show this popup on specific pages
      if (['collection', 'product'].indexOf(window.theme.request.page_type) === -1) {
        return;
      }

      // Treat geolocation as failed after ~3s of polling
      let geoFailAfter = 3000;

      const openAfterGeo = () => {
        if (geoFailAfter <= 0) {
          // Popup has open(), not show() — the old show() call threw and the
          // popup never appeared for anyone whose geolocation was blocked.
          this.popup.classList.remove('has-country');
          this.popup.open();

          return;
        }

        if (typeof(easylocation) === 'undefined'
          || !easylocation.locationData
          || !easylocation.locationData.country.iso_code
          || !easylocation.locationData.country.names
        ) {
          geoFailAfter -= 33;

          return setTimeout(() => {
            openAfterGeo();
          }, 33);
        }

        const isoCode = easylocation.locationData.country.iso_code.toUpperCase();

        if (this.isTreatmentAvailableInCountry(isoCode)) {
          // Preselect the geo-detected country but do NOT set() it yet:
          // set() persists the value and broadcasts treatment_country, and on
          // collection pages that broadcast triggers the treatment-tag
          // redirect — unloading the page and closing this popup before the
          // customer can confirm. The value persists on submit instead.
          this.select.value = isoCode;
          this.popup.querySelectorAll('[data-treatment-country]')
            .forEach(div => div.textContent = this.getCountryName(isoCode));
          this.popup.classList.add('has-country');
          this.syncPopupState();
        } else {
          this.popup.classList.remove('has-country');
        }

        this.update();
        this.popup.open();
      }

      this.update();
      openAfterGeo();
    } else {
      broadcast('treatment_country', {
        code: this.currentCountry,
        name: this.currentCountryName
      });
    }
  }

  set (isoCode)
  {
    this.currentCountry = ('' + isoCode).toUpperCase();
    this.currentCountryName = this.getCountryName(this.currentCountry);

    window.app.cache.forever('treatment_country', this.currentCountry);

    // Record what we chose so resolveFromSources() knows a disagreeing
    // metafield snapshot is our own write still propagating, not a
    // support-side change to be honoured. Expires with the grace window.
    window.app.cache.put('treatment_country_pending_sync', this.currentCountry, TreatmentCountry.PENDING_SYNC_SECONDS);

    // Persist to the customer metafield when logged in. Both storefront selectors
    // and the account "save" button flow through here, so this is the single hook.
    if (window.app.customer && typeof(window.app.customer.saveTreatmentCountry) === 'function') {
      window.app.customer.saveTreatmentCountry(this.currentCountry);
    }

    // The AU state only applies while Australia is selected.
    if (this.currentCountry !== 'AU') {
      this.setState(null);
    }

    broadcast('treatment_country_change', {
      code: this.currentCountry,
      name: this.currentCountryName
    });

    broadcast('treatment_country', {
      code: this.currentCountry,
      name: this.currentCountryName
    });
  }

  update ()
  {
    document.querySelectorAll('a').forEach(a => {
      if (a.pathname
        && a.pathname.startsWith('/collections/')
        && !a.closest('facet-remove')
        && !a.classList.contains('remove-all-filters')
      ) {
        const handle = a.pathname.split('/')[2];
        const filters = app.cache.get(handle + '_filters');

        if (this.currentCountry && handle === 'find-donors') {
          // Australia narrows to a state-specific tag (e.g. treatment-au-nsw) when a state is chosen.
          const treatmentTag = (this.currentCountry === 'AU' && this.currentState)
            ? 'treatment-' + this.currentState.toLowerCase()
            : 'treatment-' + this.currentCountry.toLowerCase();

          a.pathname = '/collections/find-donors/' + treatmentTag;
        }

        const currentSearch = new URLSearchParams(a.search);

        if (filters && currentSearch.get('auto') !== '0') {
          a.search = new URLSearchParams(filters);
        }
      }
    });

    if (this.currentCountryName) {
      document.querySelectorAll('[data-treatment-country]').forEach(div => div.textContent = this.currentCountryName);
    }

    if (this.select.value) {
      this.submit.classList.remove('disabled');
    } else {
      this.submit.classList.add('disabled');
    }

    broadcast('treatment_country_updated', {
      code: this.currentCountry,
      name: this.currentCountryName
    });
  }

  isTreatmentAvailableInCountry (isoCode)
  {
    let available = false;

    window.theme.treatmentCountries.forEach(country => {
      if (country.code === isoCode.toUpperCase()) {
        available = true;
      }
    });

    return available;
  }

  getCountryName (isoCode)
  {
    let name = null;

    window.theme.treatmentCountries.forEach(country => {
      if (country.code === isoCode.toUpperCase()) {
        name = country.name;
      }
    });

    return name;
  }

  setState (stateCode)
  {
    this.currentState = stateCode ? ('' + stateCode).toUpperCase() : null;

    if (this.currentState) {
      window.app.cache.forever('treatment_au_state', this.currentState);

      // Same propagation grace as the country (see set()).
      window.app.cache.put('treatment_au_state_pending_sync', this.currentState, TreatmentCountry.PENDING_SYNC_SECONDS);
    } else {
      window.app.cache.forget('treatment_au_state');
    }

    // Persist to the customer metafield when logged in.
    if (window.app.customer && typeof(window.app.customer.saveTreatmentState) === 'function') {
      window.app.customer.saveTreatmentState(this.currentState);
    }

    broadcast('treatment_au_state_change', {
      code: this.currentState,
      name: this.getStateName(this.currentState)
    });
  }

  // Query-string fragment for the quota/eligibility API calls: the selected AU
  // state (e.g. "&treatment_state=AU-NSW") or an empty string. The backend only
  // honours it when the treatment country is Australia, so it is safe to append
  // unconditionally.
  stateQueryParam ()
  {
    const state = this.getSelection().state;

    return state
      ? '&treatment_state=' + encodeURIComponent(state)
      : '';
  }

  // Returns the donor.market_stock sub-key to gate per-variant stock against,
  // or null when the current selection isn't stock-gated. Australia is gated
  // per state (key = the state code, e.g. "au-nsw"); other markets are gated by
  // country code only when listed in gatedCountries (e.g. "gb").
  marketStockKey (gatedCountries)
  {
    const selection = this.getSelection();
    const country = (selection.country || '').toLowerCase();

    if (!country) {
      return null;
    }

    if (country === 'au') {
      return selection.state ? selection.state.toLowerCase() : null;
    }

    return (gatedCountries || []).indexOf(country) !== -1 ? country : null;
  }

  getStateName (stateCode)
  {
    let name = null;

    if (!stateCode || !window.theme.auStates) {
      return name;
    }

    window.theme.auStates.forEach(state => {
      if (state.code === stateCode.toUpperCase()) {
        name = state.name;
      }
    });

    return name;
  }

  syncPopupState ()
  {
    if (!this.stateContainer || !this.stateSelect) {
      return;
    }

    const isAustralia = this.select.value && this.select.value.toUpperCase() === 'AU';

    if (isAustralia) {
      this.stateContainer.removeAttribute('hidden');

      if (this.currentState && this.stateSelect.value !== this.currentState) {
        this.stateSelect.value = this.currentState;
        this.stateSelect.dispatchEvent(new Event('change'));
      }
    } else {
      this.stateContainer.setAttribute('hidden', '');
    }
  }
}